// LoginStepSignIn.jsx

import React from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// CSS
import './LoginStepSignIn.css';

// Components
import Button from 'components/button/Button';
import SignupButtons from 'components/signupbuttons/SignupButtons';
import TextInput from 'components/textinput/TextInput';
import WizardStep from 'components/wizard/WizardStep';
import WizardStepContent from 'components/wizard/WizardStepContent';
import LoginLabel from 'login/label/LoginLabel';

/**
 * LoginStepSignIn
 *
 * Step 1 of the login/signup wizard, where users can sign in with email/password or navigate to signup options.
 *
 * @param {number} currentStep - Current step number in the wizard.
 * @param {string} direction - Animation direction for step transition.
 * @param {boolean} isAutoHeight - Whether the step should auto-adjust height.
 * @param {string} loginEmail - Current value of the login email input.
 * @param {string} loginPassword - Current value of the login password input.
 * @param {boolean} loginFormValid - Whether the login form inputs are valid.
 * @param {Function} handleLoginEmailChange - Handler for changes to the login email input.
 * @param {Function} handleLoginPasswordChange - Handler for changes to the login password input.
 * @param {Function} handleLogin - Handler for when the user attempts to log in.
 * @param {Function} handleSignupEmailStepClick - Handler for when the user clicks to navigate to the email signup step.
 * @param {Function} handleGoogleSignup - Handler for when the user clicks to sign up with Google.
 * @param {Function} handleForgotPasswordStepClick - Handler for navigating to forgot-password.
 *
 * @returns {ReactNode} LoginStepSignIn component.
 */
const LoginStepSignIn = ({
  currentStep,
  direction,
  isAutoHeight,
  loginEmail,
  loginPassword,
  loginFormValid,
  handleLoginEmailChange,
  handleLoginPasswordChange,
  handleLogin,
  handleSignupEmailStepClick,
  handleGoogleSignup,
  handleForgotPasswordStepClick,
}) => {

  // ----- CONTEXT ----- //

  const { theme } = useTheme();

  // ----- RENDER ----- //

  return (
    <WizardStep
      step={1}
      currentStep={currentStep}
      direction={direction}
      isAutoHeight={isAutoHeight}
    >
      <WizardStepContent>
        <div className="login-form-container">
          <div className="login-field-container">
            <LoginLabel text="Email Address" />
            <TextInput
              placeholder="Email Address..."
              value={loginEmail}
              required={true}
              type="text"
              onChange={(e) => handleLoginEmailChange(e.target.value.trim())}
            />
          </div>

          <div className="login-field-container">
            <LoginLabel text="Password" />
            <TextInput
              placeholder="Password..."
              value={loginPassword}
              required={true}
              type="password"
              onChange={(e) => handleLoginPasswordChange(e.target.value.trim())}
              onEnterPress={handleLogin}
            />
          </div>

          <div className="login-button-wrapper">
            <Button
              label="Sign In..."
              enabled={loginFormValid}
              backgroundColor={theme.buttonHoverBackgroundColor}
              fontSize={14}
              height={44}
              onClick={handleLogin}
            />
          </div>

          <div className="login-label-row">
            {/* FORGOT LINK */}
            <div
              className="login-forgot-link"
              onClick={handleForgotPasswordStepClick}
            >
              Forgot your password?
            </div>
          </div>

          <SignupButtons
            onEmailClick={handleSignupEmailStepClick}
            onGoogleClick={handleGoogleSignup}
          />

        </div>

      </WizardStepContent>
    </WizardStep>
  );
};

// Dev Tools
LoginStepSignIn.displayName = 'LoginStepSignIn';

export default React.memo(LoginStepSignIn);
